import {
  DepartmentTargetProfileAssignment,
  DepartmentTargetProfileDetail,
  SeniorityBand,
  SeniorityExpectations,
  TargetProfileCatalogItem,
  TargetProfileDetail,
  TargetProfileOccupation,
  TargetProfileSkill,
} from '../types/DepartmentTargetProfileTypes';

import {ApiSerializer} from '../../../../serializers/ApiSerializer';
import {ProfessionalArea} from '../../../../types/ProfessionalArea';

const emptySkills = (): TargetProfileSkill[] => [];

const BAND_KEY_ALIASES: Record<string, SeniorityBand> = {
  junior: 'junior',
  mid_level: 'mid_level',
  midLevel: 'mid_level',
  senior: 'senior',
  lead: 'lead',
  manager: 'manager',
};

export const normalizeSeniorityExpectations = (raw: unknown): SeniorityExpectations => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  const next: SeniorityExpectations = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    const band = BAND_KEY_ALIASES[key];
    if (!band || typeof value !== 'string') {
      return;
    }
    const text = value.trim();
    if (text) {
      next[band] = text;
    }
  });
  return next;
};

export const mapProfessionalAreasConfigResponse = (raw: unknown): ProfessionalArea[] => {
  const body = ApiSerializer.toCamelCase(raw) as ProfessionalArea[] | {professionalAreas?: ProfessionalArea[]};

  if (Array.isArray(body)) {
    return body;
  }

  return Array.isArray(body.professionalAreas) ? body.professionalAreas : [];
};

export const mapTargetProfilesCatalogResponse = (raw: unknown): TargetProfileCatalogItem[] => {
  const body = ApiSerializer.toCamelCase(raw);

  if (Array.isArray(body)) {
    return body as TargetProfileCatalogItem[];
  }

  if (body && typeof body === 'object') {
    const record = body as {targetProfiles?: TargetProfileCatalogItem[]};
    if (Array.isArray(record.targetProfiles)) {
      return record.targetProfiles;
    }
  }

  return [];
};

export const mapTargetProfileDetailResponse = (raw: unknown): TargetProfileDetail | undefined => {
  const body = ApiSerializer.toCamelCase(raw) as {targetProfile?: TargetProfileDetail};
  return body.targetProfile;
};

export const mapCompanyDepartmentTargetProfileDetailResponse = (
  raw: unknown
): DepartmentTargetProfileDetail | undefined => {
  const body = ApiSerializer.toCamelCase(raw) as {
    companyDepartmentTargetProfile?: DepartmentTargetProfileDetail & {
      seniorityExpectations?: unknown;
    };
  };
  const detail = body.companyDepartmentTargetProfile;
  if (!detail) {
    return undefined;
  }

  return {
    ...detail,
    tools: detail.tools ?? emptySkills(),
    domainKnowledge: detail.domainKnowledge ?? emptySkills(),
    seniorityExpectations: normalizeSeniorityExpectations(detail.seniorityExpectations),
  };
};

export const mapDepartmentTargetProfileDetailToDisplayProfile = (
  detail: DepartmentTargetProfileDetail
): TargetProfileDetail => {
  const occupation: TargetProfileOccupation = detail.occupation ?? {
    title: detail.occupationTitle ?? '',
    descriptionEn: '',
    descriptionDe: '',
  };

  return {
    resourceId: detail.resourceId,
    title: detail.title,
    seniority: detail.seniority ?? null,
    industry: detail.industry ?? null,
    geography: detail.geography ?? null,
    experienceLevel: detail.experienceLevel ?? null,
    version: detail.sourceVersion,
    educationRequirements: detail.educationRequirements ?? null,
    workingStyleNotes: detail.workingStyleNotes ?? null,
    occupation,
    professionalArea: detail.professionalArea,
    coreCompetencies: detail.coreCompetencies ?? emptySkills(),
    hardSkills: detail.hardSkills ?? emptySkills(),
    tools: detail.tools ?? emptySkills(),
    domainKnowledge: detail.domainKnowledge ?? emptySkills(),
    softSkills: detail.softSkills ?? emptySkills(),
    responsibilities: detail.responsibilities ?? emptySkills(),
    occupationAliases: detail.occupationAliases,
  };
};

export const mapDepartmentTargetProfilesResponse = (raw: unknown): DepartmentTargetProfileAssignment[] => {
  const body = ApiSerializer.toCamelCase(raw) as {
    companyDepartmentTargetProfiles?: DepartmentTargetProfileAssignment[];
  };
  return Array.isArray(body.companyDepartmentTargetProfiles) ? body.companyDepartmentTargetProfiles : [];
};
